import { ArrowLeft, CheckCircle } from '@untitledui/icons';
import { useCallback, useEffect, useMemo } from 'react';

import { Button } from '@/lib/base/buttons/button';
import { LoadingIndicator } from '@/lib/base/foundations/loading-indicator';
import { AlertFloating } from '@/lib/base/overlays';

import { useActiveCampaignMappings } from '../hooks/useActiveCampaignMappings';
import type { ActiveCampaignFieldMapping, AhoyPropertyOption } from '../types';
import type { MappingUpdate } from './EditableMappingRow';
import { MappingSections } from './MappingSections';

interface FieldMappingManagerProps {
  orgId: number | string;
  availableAhoyProperties: AhoyPropertyOption[];
  onBack: () => void;
}

const EMPTY_MAPPINGS: ActiveCampaignFieldMapping[] = [];

function isSyncing(m: ActiveCampaignFieldMapping): boolean {
  return !!m.ahoy_property && m.sync_direction !== 'skipped';
}

export function FieldMappingManager({
  orgId,
  availableAhoyProperties,
  onBack,
}: FieldMappingManagerProps) {
  const { mappings, isLoading, updateMapping, savingIds, errorMessagesById } =
    useActiveCampaignMappings(orgId);

  const allMappings = useMemo(
    () => mappings.data?.mappings ?? EMPTY_MAPPINGS,
    [mappings.data]
  );

  const syncingCount = useMemo(
    () => allMappings.filter(isSyncing).length,
    [allMappings]
  );

  const handleUpdate = useCallback(
    (mappingId: number, body: MappingUpdate) => {
      updateMapping.mutate({ mappingId, body });
    },
    [updateMapping]
  );

  // The mutation's success flag survives an org switch, so without this the
  // "All changes saved" indicator would show in a newly selected org with no
  // save in it. Reset the mutation on orgId change to scope success to saves
  // initiated in the current org.
  const { reset: resetUpdateMapping } = updateMapping;
  useEffect(() => {
    resetUpdateMapping();
  }, [orgId, resetUpdateMapping]);

  // A failure is never silent: normally each error renders inline on its own
  // row, but if an errored mapping isn't in the rendered list (e.g. it was
  // removed) fall back to a global alert so the user still sees it.
  const hasUnattributedError = useMemo(() => {
    if (errorMessagesById.size === 0) {
      return false;
    }
    const renderedIds = new Set(allMappings.map(m => m.id));
    for (const id of errorMessagesById.keys()) {
      if (!renderedIds.has(id)) {
        return true;
      }
    }
    return false;
  }, [errorMessagesById, allMappings]);

  const isMutating = savingIds.size > 0;
  const isMutateSuccess =
    !isMutating && updateMapping.isSuccess && errorMessagesById.size === 0;
  let saveStatus = null;
  if (isMutating) {
    saveStatus = (
      <span className='flex items-center gap-1.5 text-body-small text-[var(--color-text-secondary)]'>
        <LoadingIndicator size='xs' inline />
        Saving…
      </span>
    );
  } else if (isMutateSuccess) {
    saveStatus = (
      <span className='flex items-center gap-1.5 text-body-small text-[var(--color-text-success-primary)]'>
        <CheckCircle className='h-4 w-4' aria-hidden='true' />
        All changes saved
      </span>
    );
  }

  return (
    <div className='space-y-5'>
      <div className='flex items-start justify-between gap-4'>
        <div>
          <h3 className='text-heading-3 text-[var(--color-text-primary)]'>
            Field mappings
          </h3>
          <p className='mt-0.5 text-body-small text-[var(--color-text-secondary)]'>
            Changes save automatically and apply to future syncs.
          </p>
        </div>
        <Button
          color='secondary'
          size='sm'
          onClick={onBack}
          iconLeading={ArrowLeft}
        >
          Back
        </Button>
      </div>

      {!isLoading && !mappings.error && allMappings.length > 0 && (
        <div className='flex items-center justify-between gap-3'>
          <p className='text-body-small text-[var(--color-text-secondary)]'>
            <span className='font-semibold text-[var(--color-text-primary)]'>
              {syncingCount}
            </span>{' '}
            of {allMappings.length} fields syncing
          </p>
          <div className='min-h-[20px]'>{saveStatus}</div>
        </div>
      )}

      {mappings.error && (
        <AlertFloating
          color='error'
          title="We couldn't load field mappings"
          description='Please refresh and try again.'
        />
      )}

      {hasUnattributedError && (
        <AlertFloating
          color='error'
          title='Couldn’t save your last change'
          description='Please try again.'
        />
      )}

      {isLoading && (
        <p className='text-sm text-[var(--color-text-secondary)]'>
          Loading field mappings…
        </p>
      )}

      {!isLoading && !mappings.error && allMappings.length > 0 && (
        <MappingSections
          mappings={allMappings}
          availableAhoyProperties={availableAhoyProperties}
          savingIds={savingIds}
          errorsById={errorMessagesById}
          onUpdate={handleUpdate}
        />
      )}

      {!isLoading && !mappings.error && allMappings.length === 0 && (
        <p className='text-sm text-[var(--color-text-secondary)]'>
          No field mappings found for this integration.
        </p>
      )}
    </div>
  );
}
